import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Search, Clock, User, ChevronRight,
  Circle, ArrowLeft, AlertTriangle, Star, Filter
} from 'lucide-react';
import { API as API_BASE } from '../../lib/api';

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ScoreBadge = ({ score }) => {
  const color = score >= 7 ? 'text-emerald-400 bg-emerald-400/15 border-emerald-400/30'
    : score >= 4 ? 'text-amber-400 bg-amber-400/15 border-amber-400/30'
    : 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  return (
    <span data-testid="lead-score-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${color}`}>
      <Star className="w-2.5 h-2.5" /> {score}
    </span>
  );
};

const SessionCard = ({ session, isActive, onClick, selected }) => (
  <button
    data-testid={`session-card-${session.session_id}`}
    onClick={onClick}
    className="w-full text-left p-4 rounded-xl transition-all border"
    style={{
      background: selected ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.03)',
      borderColor: selected ? 'rgba(212,175,55,0.35)' : 'rgba(255,255,255,0.06)',
    }}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        {isActive && (
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
        )}
        <span className="text-sm font-semibold text-white truncate">
          {session.restaurant_name || session.email || `Session ${session.session_id.slice(0, 8)}`}
        </span>
      </div>
      <ScoreBadge score={session.lead_score} />
    </div>
    <p className="text-xs text-gray-500 truncate mb-2">
      {session.summary || (session.messages?.length > 0 ? session.messages[session.messages.length - 1].content?.slice(0, 80) + '...' : 'No messages')}
    </p>
    <div className="flex items-center justify-between text-[10px] text-gray-600">
      <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {session.message_count} msgs</span>
      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(session.last_message_at || session.created_at)}</span>
    </div>
  </button>
);

const MessageBubble = ({ msg, idx }) => {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.02 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
    >
      <div
        className="max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
        style={{
          background: isUser ? 'rgba(212,175,55,0.15)' : 'rgba(255,255,255,0.06)',
          color: isUser ? '#D4AF37' : '#e5e7eb',
          borderBottomRightRadius: isUser ? 4 : 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
        }}
      >
        <p className="whitespace-pre-wrap">{msg.content}</p>
        {msg.timestamp && (
          <p className="text-[9px] mt-1 opacity-40">{formatTime(msg.timestamp)}</p>
        )}
      </div>
    </motion.div>
  );
};

export default function CommunicationHub({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const scrollRef = useRef(null);

  const fetchData = () => {
    fetch(`${API_BASE}/admin/communication-hub`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return;
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [selected]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const allSessions = [
    ...(data?.active_sessions || []).map(s => ({ ...s, _live: true })),
    ...(data?.completed_sessions || []).map(s => ({ ...s, _live: false })),
  ];

  const filtered = allSessions.filter(s => {
    if (filter === 'active' && !s._live) return false;
    if (filter === 'flagged' && !s.human_followup) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (s.restaurant_name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q) ||
        (s.session_id || '').toLowerCase().includes(q) ||
        (s.summary || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const selectedSession = selected ? allSessions.find(s => s.session_id === selected) : null;

  return (
    <div data-testid="communication-hub" className="flex gap-4" style={{ height: 'calc(100vh - 260px)', minHeight: 500 }}>
      {/* Left panel — Session List */}
      <div
        className="flex flex-col rounded-2xl border overflow-hidden shrink-0"
        style={{
          width: selectedSession ? 340 : '100%',
          maxWidth: selectedSession ? 340 : '100%',
          background: 'rgba(255,255,255,0.02)',
          borderColor: 'rgba(255,255,255,0.08)',
          transition: 'width 0.3s, max-width 0.3s',
        }}
      >
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-bold text-base">Conversations</h3>
            <div className="flex items-center gap-2 text-xs">
              {data?.active_count > 0 && (
                <span className="flex items-center gap-1 text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full font-semibold">
                  <Circle className="w-2 h-2 fill-emerald-400" /> {data.active_count} live
                </span>
              )}
              <span className="text-gray-500">{data?.total_sessions || 0} total</span>
            </div>
          </div>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input
              data-testid="comm-hub-search"
              type="text"
              placeholder="Search sessions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37]/40"
            />
          </div>
          <div className="flex gap-1">
            {['all', 'active', 'flagged'].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className="px-3 py-1 rounded-lg text-xs font-medium transition-colors"
                style={{
                  background: filter === f ? 'rgba(212,175,55,0.15)' : 'transparent',
                  color: filter === f ? '#D4AF37' : '#6b7280',
                  border: filter === f ? '1px solid rgba(212,175,55,0.3)' : '1px solid transparent',
                }}
              >
                {f === 'all' ? 'All' : f === 'active' ? 'Live' : 'Flagged'}
              </button>
            ))}
          </div>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-600 text-sm">No sessions found</div>
          ) : (
            filtered.map(s => (
              <SessionCard
                key={s.session_id}
                session={s}
                isActive={s._live}
                selected={selected === s.session_id}
                onClick={() => setSelected(s.session_id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right panel — Message Thread */}
      <AnimatePresence mode="wait">
        {selectedSession && (
          <motion.div
            key={selectedSession.session_id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex-1 flex flex-col rounded-2xl border overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.08)' }}
          >
            {/* Thread header */}
            <div className="flex items-center gap-3 p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              <button
                data-testid="back-to-list"
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-white font-semibold text-sm truncate">
                    {selectedSession.restaurant_name || selectedSession.email || `Session ${selectedSession.session_id.slice(0, 8)}`}
                  </h4>
                  {selectedSession._live && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded-full font-bold">
                      LIVE
                    </span>
                  )}
                  {selectedSession.human_followup && (
                    <span className="flex items-center gap-1 text-[10px] text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full font-bold">
                      <AlertTriangle className="w-2.5 h-2.5" /> FLAGGED
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 truncate">
                  {selectedSession.agent_type} &middot; {selectedSession.message_count} messages &middot; Score: {selectedSession.lead_score}
                  {selectedSession.email && ` &middot; ${selectedSession.email}`}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
              {(selectedSession.messages || []).length === 0 ? (
                <div className="text-center py-16 text-gray-600 text-sm">No messages in this session</div>
              ) : (
                selectedSession.messages.map((msg, idx) => (
                  <MessageBubble key={idx} msg={msg} idx={idx} />
                ))
              )}
            </div>

            {/* Context footer */}
            {selectedSession.context && Object.keys(selectedSession.context).length > 0 && (
              <div className="p-3 border-t text-[10px] text-gray-600 flex flex-wrap gap-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                {Object.entries(selectedSession.context).slice(0, 6).map(([k, v]) => (
                  <span key={k} className="bg-white/5 px-2 py-0.5 rounded">
                    {k}: {typeof v === 'string' ? v : JSON.stringify(v).slice(0, 30)}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
